import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { Vehicle, VehicleHistory, TimelineData } from '../types'
import { format, subHours, startOfHour } from 'date-fns'
import { es } from 'date-fns/locale'
import { Play, Pause, SkipBack, SkipForward, Calendar } from 'lucide-react'

interface VehicleTimelineProps {
  vehicle: Vehicle | null
  history: VehicleHistory[]
  timeRange?: number
  onTimeRangeChange?: (hours: number) => void
}

const VehicleTimeline: React.FC<VehicleTimelineProps> = ({ 
  vehicle, 
  history, 
  timeRange: propTimeRange = 168,
  onTimeRangeChange 
}) => {
  const { t } = useTranslation()
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [timeRange, setTimeRange] = useState(propTimeRange)

  // Process history data for timeline
  const timelineData = useMemo(() => {
    if (!history.length) return []

    const now = new Date()
    const startTime = subHours(now, timeRange)
    
    // Group data by hour
    const hourlyData: { [key: string]: TimelineData } = {}
    
    history
      .filter(point => point.timestamp >= startTime)
      .forEach(point => {
        const hourKey = startOfHour(point.timestamp).toISOString()
        
        if (!hourlyData[hourKey]) {
          hourlyData[hourKey] = {
            timestamp: startOfHour(point.timestamp),
            speed: point.speed,
            status: point.speed > 5 ? 'moving' : point.speed > 0 ? 'idle' : 'stopped',
            location: point.location,
            event: point.event
          } as TimelineData
        } else {
          // Average speed for the hour
          hourlyData[hourKey].speed = (hourlyData[hourKey].speed + point.speed) / 2
        }
      })

    return Object.values(hourlyData).sort((a, b) => 
      a.timestamp.getTime() - b.timestamp.getTime()
    )
  }, [history, timeRange])

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying)
  }

  const handleTimeRangeChange = (hours: number) => {
    setTimeRange(hours)
    if (onTimeRangeChange) {
      onTimeRangeChange(hours)
    }
  }

  const formatTooltip = (value: any, name: string, _props: any) => {
    if (name === 'speed') {
      return [`${value} km/h`, 'Velocidad']
    }
    return [value, name]
  }

  const formatXAxisLabel = (tickItem: string) => {
    return format(new Date(tickItem), 'HH:mm', { locale: es })
  }

  if (!vehicle) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">{t('select_vehicle_timeline')}</p>
      </div>
    )
  }

  return (
    <div className="h-full bg-white border-t border-gray-200">
      {/* Timeline Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <h3 className="font-medium text-gray-800">
            {t('timeline')} — {vehicle.name}
          </h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={handlePlayPause}
              className="p-1 rounded hover:bg-gray-100"
            >
              {isPlaying ? (
                <Pause className="w-4 h-4" />
              ) : (
                <Play className="w-4 h-4" />
              )}
            </button>
            <button className="p-1 rounded hover:bg-gray-100">
              <SkipBack className="w-4 h-4" />
            </button>
            <button className="p-1 rounded hover:bg-gray-100">
              <SkipForward className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Time Range Controls */}
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <select
            value={timeRange}
            onChange={(e) => handleTimeRangeChange(Number(e.target.value))}
            className="text-sm border border-gray-300 rounded px-2 py-1"
          >
            <option value={1}>{t('last_hour')}</option>
            <option value={6}>{t('last_6_hours')}</option>
            <option value={12}>{t('last_12_hours')}</option>
            <option value={24}>{t('last_24_hours')}</option>
            <option value={72}>{t('last_3_days')}</option>
            <option value={168}>Last 7 Days</option>
            <option value={336}>Last 2 Weeks</option>
            <option value={720}>Last 30 Days</option>
          </select>
        </div>
      </div>

      {/* Timeline Chart */}
      <div className="h-20 p-2">
        {timelineData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatXAxisLabel}
                stroke="#6b7280"
                fontSize={12}
              />
              <YAxis
                domain={[0, 'dataMax + 10']}
                stroke="#6b7280"
                fontSize={12}
                width={40}
              />
              <Tooltip
                formatter={formatTooltip}
                labelFormatter={(label) => 
                  format(new Date(label), 'dd/MM/yyyy HH:mm', { locale: es })
                }
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '12px'
                }}
              />
              <Line
                type="monotone"
                dataKey="speed"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6', strokeWidth: 0, r: 2 }}
                activeDot={{ r: 4, stroke: '#3b82f6', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500 text-sm">
            {t('no_timeline_data')}
          </div>
        )}
      </div>

      {/* Timeline Status Bar */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <div className="flex items-center space-x-4">
            <span>Current Speed: {vehicle.speed} km/h</span>
            <span>Status: {vehicle.status}</span>
            {vehicle.location.address && (
              <span>Location: {vehicle.location.address}</span>
            )}
          </div>
          <div>
            Last Update: {new Date(vehicle.lastUpdate).toLocaleDateString('en-GB')} {new Date(vehicle.lastUpdate).toLocaleTimeString('en-US', { 
              hour12: true,
              hour: 'numeric',
              minute: '2-digit',
              second: '2-digit'
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default VehicleTimeline
